"use client"

// Renders the line_items table inside an expanded bundle Instance row.
// Shared between the Bundle × SKU Matrix decompose Sheet and the Bundle
// Performance drill-down — both expose «what was actually in this bundle
// instance» the same way.

import type { BundleInstanceLineItem } from "@/lib/shopify/bundles/types"

const fmtCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
const fmtInt = new Intl.NumberFormat("en-US")

export interface InstanceLineItemsBreakdownProps {
  lineItems: ReadonlyArray<BundleInstanceLineItem>
  /**
   * `true` for bundle_sku strategy: the instance is a SINGLE real line_item
   * (the kit's identifier SKU); composition components are virtual and live
   * in the Recipe tab. Surfaced as an italic note so users don't think the
   * components are missing. Optional — callers that don't yet plumb strategy
   * through can omit it and the note is simply not shown.
   */
  isBundleSku?: boolean
}

export function InstanceLineItemsBreakdown({
  lineItems,
  isBundleSku = false,
}: InstanceLineItemsBreakdownProps) {
  if (lineItems.length === 0) {
    return (
      <p className="px-3 py-2 text-xs italic text-muted-foreground">
        No line items recorded for this instance.
      </p>
    )
  }
  return (
    <div className="px-3 py-2">
      {isBundleSku && (
        <p className="text-[11px] italic text-muted-foreground mb-1.5">
          Single real line item — the kit&apos;s identifier SKU. Composition
          components are virtual (see Recipe tab).
        </p>
      )}
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left px-2 py-1 font-medium">Product</th>
            <th className="text-left px-2 py-1 font-medium">SKU</th>
            <th className="text-right px-2 py-1 font-medium">Qty</th>
            <th className="text-right px-2 py-1 font-medium">Line total</th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => (
            <tr
              key={`${li.product_id ?? "_"}|${li.variant_id ?? "_"}|${li.sku ?? "_"}|${idx}`}
              className="border-t border-muted/30"
            >
              <td className="px-2 py-1 align-top">
                <div className="font-medium break-words">{li.title || "—"}</div>
                {li.variant_title && (
                  <div className="text-[10px] text-muted-foreground break-words">
                    {li.variant_title}
                  </div>
                )}
              </td>
              <td className="px-2 py-1 align-top font-mono text-[11px] break-all">
                {li.sku ?? "—"}
              </td>
              <td className="px-2 py-1 align-top text-right tabular-nums">
                {fmtInt.format(li.quantity)}
              </td>
              <td className="px-2 py-1 align-top text-right tabular-nums">
                {fmtCurrency.format(li.line_total)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
